// The `addendum-lifecycle` event is broadcast to the whole practice room, which includes the session
// that caused it, and carries no identifiers (the room is unfiltered by chart permissions), so the
// listener cannot tell its own echo from someone else's write. This marker supplies that bit locally:
// the mutation stamps a window, the listener skips an event inside it rather than refetching the
// roots the mutation just invalidated.
//
// The window opens when the REQUEST GOES OUT, not when it answers: the backend emits right after the
// transaction commits and only then returns, so the broadcast reaches this session over the open
// socket BEFORE the HTTP response does. Stamping on success alone always lost that race and every
// sign refetched the addendum lists twice — once from the mutation, once from the echo.
//
// Tradeoff: a genuine remote event landing inside the window is dropped. Both stale-draft backstops
// survive — focus refetch, and a server-side rejection (code 5/8) of any write against a dead draft.
// Keep the window short.
const ECHO_WINDOW_MS = 2000;

// A request that never settles (dropped connection, a `finally` that never runs) must not suppress
// remote events for the rest of the session, so an in-flight claim expires on its own.
const MAX_INFLIGHT_MS = 30000;

let localLifecycleAt = 0;
let inFlight = 0;
let inFlightSince = 0;

/** Call right BEFORE a create / sign / discard request leaves this session. Pair with the end call. */
export const beginLocalAddendumLifecycle = () => {
  if (inFlight === 0) inFlightSince = Date.now();
  inFlight += 1;
};

/**
 * Call once the request settles — on failure too, or the claim leaks until it expires. Keeps the
 * window open a moment longer, covering a broadcast that arrives just after the response.
 */
export const markLocalAddendumLifecycle = () => {
  if (inFlight > 0) inFlight -= 1;
  localLifecycleAt = Date.now();
};

/** Is an incoming `addendum-lifecycle` event this session's own broadcast coming back? */
export const isLocalAddendumLifecycleEcho = () => {
  if (inFlight > 0) {
    if (Date.now() - inFlightSince < MAX_INFLIGHT_MS) return true;
    inFlight = 0;
  }
  return Date.now() - localLifecycleAt < ECHO_WINDOW_MS;
};
